import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { EnhancedLoadingService } from '../../services/enhanced-loading.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-datasafra-search',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './datasafra-search.component.html',
  styleUrl: './datasafra-search.component.scss',
})
export class DatasafraSearchComponent implements AfterViewInit {
  loadingService = inject(EnhancedLoadingService);

  readonly searchControl = new FormControl<string>('');
  readonly onlyMineControl = new FormControl<boolean>(true, {
    nonNullable: true,
  });

  readonly range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  readonly form = new FormGroup({
    searchText: this.searchControl,
    range: this.range,
    onlyMine: this.onlyMineControl,
  });

  @Output() search = new EventEmitter<{
    searchText?: string;
    range?: { start?: Date; end?: Date };
    onlyMine: boolean;
  }>();

  ngAfterViewInit() {
    // Debounce na busca por texto com feedback visual
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        // Mostrar loading no input durante a busca
        this.loadingService.show('input');

        this.emitSearch(searchText);

        // Simular fim da busca (na prática, o componente pai deve controlar)
        setTimeout(() => {
          this.loadingService.hide('input');
        }, 500);
      });

    // Mudanças na data também emitem busca
    this.range.valueChanges.subscribe(() => {
      this.emitSearch();
    });

    // Mudanças no checkbox onlyMine também emitem busca
    this.onlyMineControl.valueChanges.subscribe(() => {
      this.emitSearch();
    });

    // Anuncia o estado inicial (valores default dos FormControls) para o
    // pai, já que valueChanges não dispara para o valor inicial dos forms.
    this.emitSearch();
  }

  onSearch() {
    this.emitSearch();
  }

  private emitSearch(text: string | null = this.searchControl.value) {
    this.search.emit({
      searchText: text || undefined,
      range: {
        start: this.range.value.start
          ? this.setStartOfDay(this.range.value.start)
          : undefined,
        end: this.range.value.end
          ? this.setEndOfDay(this.range.value.end)
          : undefined,
      },
      onlyMine: this.onlyMineControl.value,
    });
  }
  private setStartOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  private setEndOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  }

  clearText() {
    this.form.controls.searchText.reset();
  }
}

export type SearchParams = DatasafraSearchComponent['form']['value'];
